import { useEffect, useRef, useMemo, type ReactNode } from "react";
import { useAuthStore } from "../store/useAuthStore";
import {
  isThemeDebugOverrideActive,
  subscribeToThemeDebugEvents,
  useTheme,
} from "@repo/tokens";
import { themeMap } from "../../constants";

const ThemeInitializer = () => {
  const { setTheme, themeName, setSubjectKey, forceSync } = useTheme();
  const token = useAuthStore((state) => state.token);
  const { loginMethod } = useAuthStore();
  const profilesData = useAuthStore((state) => state.profilesData);
  const themeNameRef = useRef(themeName);
  const prevThemeRef = useRef(themeName);
  themeNameRef.current = themeName;

  const verticalId = useMemo(() => {
    const profiles = profilesData?.unified_account_details?.profiles ?? [];

    const profile =
      loginMethod !== "admission_id"
        ? (profiles.find((p) => p.is_default_profile) ??
          profiles?.find((profile) => profile.vertical_id === 1) ??
          profiles[0])
        : (profiles.find((p) => p.is_default_profile) ??
          profiles?.find((profile) => profile.vertical_id !== 1) ??
          profiles[0]);

    return profile?.vertical_id;
  }, [profilesData, loginMethod]);

  useEffect(() => {
    const isDebugActive = isThemeDebugOverrideActive();
    const targetTheme =
      token && verticalId ? themeMap[verticalId] || "learn" : "learn";

    prevThemeRef.current = themeName;

    if (!token) {
      if (themeName !== "learn") {
        setTheme("learn");
        setSubjectKey(null);
      } else {
        forceSync();
        setSubjectKey(null);
      }
      return;
    }

    if (isDebugActive) {
      return;
    }

    if (themeName !== targetTheme) {
      setTheme(targetTheme);
      setSubjectKey(null);
    }
  }, [token, verticalId, themeName, setTheme, setSubjectKey, forceSync]);

  useEffect(() => {
    if (import.meta.env.PROD) {
      return;
    }
    return subscribeToThemeDebugEvents((detail) => {
      if (!detail.cleared) {
        return;
      }
      queueMicrotask(() => {
        if (isThemeDebugOverrideActive()) return;

        const tok = useAuthStore.getState().token;
        const pd = useAuthStore.getState().profilesData;
        const profiles = pd?.unified_account_details?.profiles ?? [];
        const vid =
          profiles.find((p) => p.is_default_profile)?.vertical_id ??
          profiles[0]?.vertical_id;

        const target = tok && vid ? themeMap[vid] || "learn" : "learn";

        if (themeNameRef.current !== target) {
          setTheme(target);
        }
      });
    });
  }, [setTheme]);

  return null;
};
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  return (
    <>
      <ThemeInitializer />
      {children}
    </>
  );
};
